import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, from, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

interface LocationResponse {
  latitude: number; 
  longitude: number; 
  source: string
}

interface IpApiResponse {
  latitude: number; 
  longitude: number; 
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private ipApiUrl = 'https://ipapi.co/json/';
  private api_base_url = environment.api_base_url;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {}

  getLocation(): Observable<LocationResponse> {
    return this.checkPermission().pipe(
      switchMap((status) => {
        console.log('Permission status:', status);

        if (status === 'granted') {
          return this.getGpsLocation();
        } else if (status === 'prompt') {
          return this.getGpsLocation(); 
        } else {
          console.warn('Permission denied, using IP-based location');
          return this.getIpLocation();
        }
      }),
      catchError((error) => {
        console.error('Error getting location, using IP-based location:', error);
        return this.getIpLocation(); 
      })
    );
  }

  private checkPermission(): Observable<PermissionState> {
    if (!isPlatformBrowser(this.platformId)) {
      return of('denied' as PermissionState);
    }
    return from(
      navigator.permissions.query({ name: 'geolocation' as PermissionName })
    ).pipe(
      switchMap((permissionStatus) => from([permissionStatus.state]))
    );
  }

  private getGpsLocation(): Observable<LocationResponse> {
    return new Observable<LocationResponse>(observer => {
      if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
        observer.error('Geolocation is not supported by your browser, will use IP-based location');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          observer.next({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source: 'GPS',
          });
          observer.complete();
        },
        (error) => {
          console.warn('Geolocation failed, using IP-based location', error);
          observer.error(error);
        }
      );
    }).pipe(
      catchError((error: Error) => {
        console.error('GPS failed, using IP location:', error);
        return this.getIpLocation();
      })
    )
  }

  private getIpLocation(): Observable<LocationResponse> {
    return this.http.get<IpApiResponse>(this.ipApiUrl).pipe(
      switchMap((data: any) => {
        if (data && data.latitude && data.longitude) {
          const response = { 
            latitude: data.latitude, 
            longitude: data.longitude, 
            source: 'IP' 
          };

          return from([response]);
        }
        return throwError(() => new Error('Could not fetch IP-based location'));
      }),
      catchError((error) => throwError(() => new Error('IP-based location failed: ' + error.message)))
    );
  }

  getLocations(): Observable<any> {
    const path = `${this.api_base_url}/api/cms/locations`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    
    return this.http.get(path, { headers }).pipe(
      take(1),
      map((response: any) => response.data)
    );
  }

  getLocationsSuggestion(): Observable<any> {
    const path = `${this.api_base_url}/api/cms/locations/cities`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    
    return this.http.get(path, { headers }).pipe(
      take(1),
      map((response: any) => response.data)
    );
  }

  getHomepageLocations(): Observable<any> {
    const path = `${this.api_base_url}/api/locations`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    
    return this.http.get(path, { headers }).pipe(
      take(1),
      map((response: any) => response.data)
    );
  }

  createLocation(location: any): Observable<any> {
    const path = `${this.api_base_url}/api/cms/locations`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    return this.http.post(path, location, { headers });
  }

  createLocations(locations: any[]): Observable<any> {
    const payload =  { locations };
    const path = `${this.api_base_url}/api/cms/locations/bulk-create`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    return this.http.post(path, payload, { headers });
  }

  editLocation(id: string, loaction: any): Observable<any> {
    const path = `${this.api_base_url}/api/cms/locations/${id}`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json'});
    
    return this.http.patch(path, loaction, { headers }).pipe(
      take(1),
      tap((response: any) => {
        console.log("response ", response);
      })
    );
  }

  deleteLocation(id: string): Observable<any> {
    const path = `${this.api_base_url}/api/cms/locations/${id}`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    
    return this.http.delete(path, { headers }).pipe(
      take(1),
      tap((response: any) => {
        console.log("response ", response);
      })
    );
  }
}
